import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

import sentryAiImg      from '../../assets/sentryone/image3.png';
import sentryVmsImg     from '../../assets/sentryone/image2.png';
import dropVaultImg     from '../../assets/sentryone/image1.png';


import DropVault from '../../assets/pdf/DropVault.pdf';
import SentryAI from '../../assets/pdf/SentryAI.pdf';
import SentryVMS from '../../assets/pdf/SentryVMS.pdf';
// Map each product id → the matching tab id in SentryFunctionalities
const PRODUCT_TO_TAB = {
  'sentry-vms': 'vms',
  'sentry-ai':  'ai-analytics',
  'dropvault':  'storage',
};

const products = [
  {
    id: 'sentry-vms',
    tag: 'Enterprise Video Management',
    title: 'Sentry VMS',
    subtitle: 'Intelligent Video Management System',
    image: sentryVmsImg,
    keypoints: [
      {
        icon: 'fa fa-video-camera',
        heading: '5,000+ Camera Scalability',
        text: 'Start with 5,000 cameras and grow to 15,000+. Each recording server manages up to 1,500 HD cameras — double the industry standard.',
      },
      {
        icon: 'fa fa-plug',
        heading: 'True Ecosystem Integration',
        text: 'Native drivers for 800+ manufacturers, ONVIF certified, connects with access control and building management under one interface.',
      },
    ],
    brochure: SentryVMS,
    brochureLabel: 'Download Brochure',
    accent: '#15803d',
    accentLight: '#dcfce7',
    accentMid: '#bbf7d0',
    tagColor: '#166534',
  },
  {
    id: 'sentry-ai',
    tag: 'AI-Powered Surveillance',
    title: 'Sentry AI Pro',
    subtitle: 'Advanced AI Video Analytics',
    image: sentryAiImg,
    keypoints: [
      {
        icon: 'fa fa-eye',
        heading: 'Real-Time Intelligence',
        text: 'AI capabilities — from weapon detection to fire & smoke alerts — processed live from RTSP streams without heavy storage overhead.',
      },
      {
        icon: 'fa fa-bell',
        heading: 'Automated Rule Enforcement',
        text: 'Restricted zone monitoring, crowd detection, and after-hours surveillance with instant SMS / Email / App notifications.',
      },
    ],
    brochure: SentryAI,
    brochureLabel: 'Download Brochure',
    accent: '#1d4ed8',
    accentLight: '#dbeafe',
    accentMid: '#bfdbfe',
    tagColor: '#1e40af',
  },
  {
    id: 'dropvault',
    tag: 'Secure Storage & Sharing',
    title: 'DropVault',
    subtitle: 'Secure Video Storage & Sharing Server',
    image: dropVaultImg,
    keypoints: [
      {
        icon: 'fa fa-lock',
        heading: 'Military-Grade Security',
        text: 'WORM storage, ransomware detection, encrypted transfer, AD/LDAP integration, and immutability controls for government-grade compliance.',
      },
      {
        icon: 'fa fa-cloud',
        heading: 'Centralized Intelligence',
        text: 'Single dashboard across all locations with high-availability architecture, automatic failover, and petabyte-scale object storage.',
      },
    ],
    brochure: DropVault,
    brochureLabel: 'Download Brochure',
    accent: '#a16207',
    accentLight: '#fef9c3',
    accentMid: '#fde68a',
    tagColor: '#92400e',
  },
];

/* ── helper: scroll to functionalities section and switch tab ── */
const handleExplore = (productId) => {
  const tabId = PRODUCT_TO_TAB[productId];
  if (tabId) {
    window.dispatchEvent(new CustomEvent('sentry-select-tab', { detail: { tabId } }));
  }
  const target = document.getElementById('sentry-functionalities');
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

/* ── Fully Responsive Card ── */
const ProductCard = ({ product, index }) => {
  const isReverse = index % 2 !== 0;

  return (
    <div
      className="col-xs-12 sp-card-wrapper"
      data-aos="fade-up"
      data-aos-duration="800"
      data-aos-delay={index * 100}
    >
      {/* Faded index number */}
      <div className="sp-index-number" style={{ color: product.accent }}>
        {String(index + 1).padStart(2, '0')}
      </div>

      <div
        className={`sp-card ${isReverse ? 'sp-card--reverse' : ''}`}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'translateY(-6px)';
          e.currentTarget.style.boxShadow = '0 20px 50px rgba(15,23,42,0.13)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 28px rgba(15,23,42,0.08)';
        }}
      >
        {/* ── Image ── */}
        <div className="sp-img-wrap">
          <img src={product.image} alt={product.title} className="sp-img" />
          <div
            className="sp-img-overlay"
            style={{
              background: `linear-gradient(to bottom, transparent 40%, ${product.accentLight}cc)`,
            }}
          />
        </div>

        {/* ── Content ── */}
        <div className="sp-body">

          <span
            className="sp-tag"
            style={{ background: product.accentLight, color: product.tagColor }}
          >
            {product.tag}
          </span>

          <h3 className="sp-title">{product.title}</h3>
          <p className="sp-subtitle">{product.subtitle}</p>

          <div className="sp-divider" style={{ background: product.accentMid }} />

          <ul className="sp-keypoints">
            {product.keypoints.map((kp, i) => (
              <li key={i} className="sp-kp">
                <div
                  className="sp-kp-icon"
                  style={{ background: product.accentLight, color: product.accent }}
                >
                  <i className={kp.icon} />
                </div>
                <div className="sp-kp-text">
                  <strong className="sp-kp-heading" style={{ color: product.accent }}>
                    {kp.heading}
                  </strong>
                  <p className="sp-kp-body">{kp.text}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="sp-btn-row">
            <a
              href={product.brochure}
                download={`${product.title.replace(/\s+/g, '-')}.pdf`}
              className="sp-btn sp-btn--outline"
              style={{ borderColor: product.accent, color: product.accent }}
              onMouseEnter={e => {
                e.currentTarget.style.background = product.accent;
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = product.accent;
              }}
            >
              <i className="fa fa-download" />
              {product.brochureLabel}
            </a>

            <button
              className="sp-btn sp-btn--solid"
              style={{
                background: product.accent,
                boxShadow: `0 4px 14px ${product.accent}40`,
              }}
              onClick={() => handleExplore(product.id)}
              onMouseEnter={e => {
                e.currentTarget.style.filter = 'brightness(1.12)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.filter = 'brightness(1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <i className="fa fa-th-list" />
              Explore Features
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

/* ── Main component ── */
export const SentryProducts = () => {
  useEffect(() => {
    AOS.init({ duration: 900, once: true });
  }, []);

  return (
    <section className="sp-section">

      <style>{`
        /* ───────── Section ───────── */
        .sp-section {
          background: linear-gradient(160deg, #f8fafc 0%, #f1f5f9 50%, #f0f9ff 100%);
          padding: 90px 0 70px;
          position: relative;
          overflow: hidden;
        }
        .sp-section::before {
          content: '';
          position: absolute;
          top: -160px; right: -160px;
          width: 520px; height: 520px;
          border-radius: 50%;
          background: radial-gradient(circle, #dbeafe 0%, transparent 70%);
          pointer-events: none;
        }
        .sp-section::after {
          content: '';
          position: absolute;
          bottom: -120px; left: -120px;
          width: 420px; height: 420px;
          border-radius: 50%;
          background: radial-gradient(circle, #dcfce7 0%, transparent 70%);
          pointer-events: none;
        }

        /* ───────── Header ───────── */
        .sp-header {
          text-align: center;
          margin-bottom: 64px;
          position: relative;
          z-index: 1;
        }
        .sp-eyebrow {
          display: inline-block;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: #6366f1;
          background: #eef2ff;
          padding: 4px 14px;
          border-radius: 20px;
          margin-bottom: 16px;
        }
        .sp-header h2 {
          color: #0f172a;
          font-size: 38px;
          font-weight: 800;
          margin-bottom: 14px;
          line-height: 1.2;
        }
        .sp-header p {
          color: #64748b;
          font-size: 16px;
          max-width: 520px;
          margin: 0 auto;
          line-height: 1.75;
        }

        /* ───────── Card wrapper ───────── */
        .sp-card-wrapper {
          position: relative;
          margin-bottom: 52px;
        }

        /* ───────── Faded number ───────── */
        .sp-index-number {
          position: absolute;
          top: -14px;
          left: 16px;
          font-size: 80px;
          font-weight: 900;
          line-height: 1;
          opacity: 0.06;
          pointer-events: none;
          user-select: none;
          z-index: 0;
          letter-spacing: -4px;
        }

        /* ───────── Card ───────── */
        .sp-card {
          display: flex;
          flex-direction: row;
          background: #ffffff;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 4px 28px rgba(15,23,42,0.08);
          border: 1px solid #e2e8f0;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          position: relative;
          z-index: 1;
        }
        .sp-card--reverse { flex-direction: row-reverse; }

        /* ───────── Image ───────── */
        .sp-img-wrap {
          flex: 0 0 44%;
          position: relative;
          overflow: hidden;
          min-height: 340px;
        }
        .sp-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease;
        }
        .sp-card:hover .sp-img { transform: scale(1.04); }
        .sp-img-overlay {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }

        /* ───────── Body ───────── */
        .sp-body {
          flex: 1;
          padding: 40px 44px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .sp-tag {
          display: inline-block;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 2px;
          text-transform: uppercase;
          padding: 4px 13px;
          border-radius: 20px;
          margin-bottom: 16px;
          align-self: flex-start;
        }
        .sp-title {
          color: #0f172a;
          font-size: 28px;
          font-weight: 800;
          margin-bottom: 4px;
          line-height: 1.2;
        }
        .sp-subtitle {
          color: #94a3b8;
          font-size: 13px;
          font-style: italic;
          margin-bottom: 20px;
        }
        .sp-divider {
          height: 3px;
          width: 48px;
          border-radius: 3px;
          margin-bottom: 22px;
        }

        /* ───────── Key points ───────── */
        .sp-keypoints {
          list-style: none;
          padding: 0;
          margin: 0 0 28px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .sp-kp { display: flex; gap: 14px; align-items: flex-start; }
        .sp-kp-icon {
          width: 40px; height: 40px;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          font-size: 15px;
        }
        .sp-kp-text { flex: 1; }
        .sp-kp-heading {
          display: block;
          font-size: 13px;
          font-weight: 700;
          margin-bottom: 3px;
        }
        .sp-kp-body {
          color: #64748b;
          font-size: 13px;
          line-height: 1.65;
          margin: 0;
        }

        /* ───────── Button row ───────── */
        .sp-btn-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        .sp-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 11px 22px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.4px;
          cursor: pointer;
          transition: filter 0.25s ease, transform 0.25s ease, background 0.2s ease, color 0.2s ease;
          text-decoration: none !important;
        }
        .sp-btn--solid {
          color: #ffffff !important;
          border: none;
        }
        .sp-btn--outline {
          background: transparent;
          border: 1.5px solid;
        }

        /* ═══════ RESPONSIVE ═══════ */
        @media (max-width: 1024px) {
          .sp-header h2 { font-size: 32px; }
          .sp-body { padding: 32px 36px; }
          .sp-title { font-size: 24px; }
          .sp-img-wrap { min-height: 300px; }
        }
        @media (max-width: 768px) {
          .sp-section { padding: 60px 0 50px; }
          .sp-card, .sp-card--reverse { flex-direction: column !important; }
          .sp-img-wrap { flex: none !important; width: 100%; min-height: 240px; height: 240px; }
          .sp-body { padding: 28px 28px 32px; }
          .sp-title { font-size: 22px; }
          .sp-index-number { font-size: 56px; }
        }
        @media (max-width: 480px) {
          .sp-section { padding: 48px 0 40px; }
          .sp-header h2 { font-size: 22px; }
          .sp-card-wrapper { margin-bottom: 36px; }
          .sp-img-wrap { min-height: 200px; height: 200px; }
          .sp-body { padding: 22px 20px 26px; }
          .sp-title { font-size: 20px; }
          .sp-btn { font-size: 12px; padding: 10px 16px; flex: 1; justify-content: center; }
          .sp-btn-row { flex-direction: column; }
          .sp-index-number { font-size: 44px; top: -10px; left: 10px; }
        }
      `}</style>

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>

        <div className="sp-header" data-aos="fade-up">
          <span className="sp-eyebrow">Our Platform Suite</span>
          <h2>Integrated Security & Intelligence</h2>
          <p>
            Three purpose-built products covering every layer of modern
            enterprise security — from AI analytics to storage governance.
          </p>
        </div>

        <div className="row">
          {products.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>

      </div>
    </section>
  );
};

export default SentryProducts;